'use client'

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { useAgentWebSocket } from '@/hooks/useAgentWebSocket'
import { apiClient } from '@/lib/api-client'
import { toast } from '@/components/ui/toast'

// Types for chat messages
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  toolCalls?: ToolCall[]
  isLoading?: boolean
  isHumanAgent?: boolean  // Flag to indicate message is from a human agent (not AI)
  animate?: boolean       // Flag to trigger typewriter effect
}

export interface ToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

// Types for policies
export interface AIPolicy {
  id: string
  name: string
  description: string
  naturalLanguage: string
  dsl: PolicyDSL | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface PolicyDSL {
  conditions: PolicyCondition[]
  actions: PolicyAction[]
  match_mode?: 'all' | 'any'
  stop_on_match?: boolean
}

export interface PolicyCondition {
  field: string
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'between' | 'in' | 'not_in' | 'matches' | 'is_null' | 'is_not_null'
  value: string | number | boolean | unknown[] | null
  field_path?: string
}

export interface PolicyAction {
  type: 'auto_approve' | 'auto_reject' | 'flag_review' | 'notify' | 'set_status' | 'set_field' | 'add_note' | 'add_tag' | 'escalate' | 'custom'
  value?: string | number | boolean | Record<string, unknown>
  params?: Record<string, unknown>
}

// Types for insights
export interface AIInsight {
  id: string
  type: 'pattern' | 'anomaly' | 'recommendation'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  data?: Record<string, unknown>
  suggestedAction?: string
  createdAt: Date
}

// Context state type
interface AIContextState {
  // AI Manager state
  isManagerOpen: boolean
  openManager: () => void
  closeManager: () => void
  toggleManager: () => void
  
  // Chat state
  chatHistory: ChatMessage[]
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  removeLoadingMessage: () => void
  clearHistory: () => void
  isTyping: boolean
  setIsTyping: (typing: boolean) => void
  sessionId: string  // Session UUID for chat conversations
  
  // Page context
  currentPageContext: string
  
  // Policies state
  policies: AIPolicy[]
  setPolicies: (policies: AIPolicy[]) => void
  addPolicy: (policy: AIPolicy) => void
  updatePolicy: (id: string, updates: Partial<AIPolicy>) => void
  removePolicy: (id: string) => void
  
  // Insights state
  insights: AIInsight[]
  setInsights: (insights: AIInsight[]) => void
  pendingInsightsCount: number
  markInsightAsRead: (id: string) => void
  
  // Suggestions
  hasPendingSuggestions: boolean
  setHasPendingSuggestions: (has: boolean) => void

  // Unread Agent Messages State
  unreadSessions: Set<string>
  addUnreadSession: (id: string) => void
  removeUnreadSession: (id: string) => void
  activeConversationId: string | null
  setActiveConversationId: (id: string | null) => void
}

const AIContext = createContext<AIContextState | undefined>(undefined)

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within an AIProvider')
  }
  return context
}

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// Helper component to manage background WS connections
function AgentWSListener({ sessionId, onMessage }: { sessionId: string, onMessage: (msg: any) => void }) {
  useAgentWebSocket(sessionId, onMessage);
  return null;
}

export function AIProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { status } = useSession()
  
  // AI Manager state
  const [isManagerOpen, setIsManagerOpen] = useState(false)
  
  // Chat state
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [isTyping, setIsTyping] = useState(false)
  
  // Session ID - persists for the browser session (generates new UUID on page refresh)
  const [sessionId] = useState(() => crypto.randomUUID())
  
  // Policies state
  const [policies, setPolicies] = useState<AIPolicy[]>([])
  
  // Insights state
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [readInsightIds, setReadInsightIds] = useState<Set<string>>(new Set())
  
  // Suggestions state
  const [hasPendingSuggestions, setHasPendingSuggestions] = useState(false)

  // Unread & Active Session tracking
  const [unreadSessions, setUnreadSessions] = useState<Set<string>>(new Set())
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [recentSessionIds, setRecentSessionIds] = useState<string[]>([])
  
  // Derive page context from pathname
  const currentPageContext = pathname || '/'
  
  // Manager controls
  const openManager = useCallback(() => setIsManagerOpen(true), [])
  const closeManager = useCallback(() => setIsManagerOpen(false), [])
  const toggleManager = useCallback(() => setIsManagerOpen(prev => !prev), [])
  
  // Chat controls
  const removeLoadingMessage = useCallback(() => {
    setChatHistory(prev => prev.filter(m => !m.isLoading))
  }, [])

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: new Date(),
    }
    
    setChatHistory(prev => {
      if (message.role === 'assistant' && !message.isLoading) {
        // Prevent exact content duplication (safeguard against HTTP & WS race conditions)
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content === message.content && !lastMsg.isLoading) {
          return prev;
        }
        return [...prev.filter(m => !m.isLoading), newMessage];
      } else {
        return [...prev, newMessage];
      }
    });
  }, [])

  // Listen for Live Agent/AI WebSocket responses (for the global AIManager)
  const handleAgentMessage = useCallback((data: any) => {
    if (data.content && data.content.trim() !== "") {
      addMessage({
        role: 'assistant',
        content: data.content,
        isLoading: false,
        isHumanAgent: true,  // Messages via WebSocket are from human agents
        animate: true        // Animate incoming WebSocket messages
      });
      setIsTyping(false);
    }
  }, [addMessage]);

  useAgentWebSocket(sessionId, handleAgentMessage);

  const clearHistory = useCallback(() => {
    setChatHistory([])
  }, [])
  
  // Policy controls
  const addPolicy = useCallback((policy: AIPolicy) => {
    setPolicies(prev => [...prev, policy])
  }, [])
  
  const updatePolicy = useCallback((id: string, updates: Partial<AIPolicy>) => {
    setPolicies(prev => 
      prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p)
    )
  }, [])
  
  const removePolicy = useCallback((id: string) => {
    setPolicies(prev => prev.filter(p => p.id !== id))
  }, [])
  
  // Insights controls
  const pendingInsightsCount = insights.filter(i => !readInsightIds.has(i.id)).length
  
  const markInsightAsRead = useCallback((id: string) => {
    setReadInsightIds(prev => new Set([...prev, id]))
  }, [])

  // === Global Notification Handlers ===
  const addUnreadSession = useCallback((id: string) => {
    setUnreadSessions(prev => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  const removeUnreadSession = useCallback((id: string) => {
    setUnreadSessions(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  // Fetch recent sessions on mount to listen globally for human agent replies
  useEffect(() => {
    if (status === 'authenticated') {
      apiClient.get('/api/ai/sessions').then((data: any) => {
        if (Array.isArray(data)) {
          // Listen to the top 5 most recent sessions
          setRecentSessionIds(data.slice(0, 5).map(s => s.id))
        }
      }).catch(console.error)
    }
  }, [status])

  // Handles messages that come into background sessions
  const handleGlobalMessage = useCallback((data: any) => {
    const msgSessionId = data.sessionId;
    if (data.content && data.content.trim() !== "" && data.role === 'assistant') {
      // If the message is for a session that is NOT currently actively open
      if (msgSessionId !== activeConversationId && msgSessionId !== sessionId) {
        if (!unreadSessions.has(msgSessionId)) {
          toast.info('New message from Support', { 
            description: 'A human agent has replied to your conversation.' 
          });
          addUnreadSession(msgSessionId);
        }
      }
    }
  }, [activeConversationId, sessionId, unreadSessions, addUnreadSession])
  
  // Keyboard shortcut for AI Manager (Cmd+J or Ctrl+J)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleManager()
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleManager])
  
  const value: AIContextState = {
    // Manager
    isManagerOpen,
    openManager,
    closeManager,
    toggleManager,
    
    // Chat
    chatHistory,
    addMessage,
    removeLoadingMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    sessionId,
    
    // Page context
    currentPageContext,
    
    // Policies
    policies,
    setPolicies,
    addPolicy,
    updatePolicy,
    removePolicy,
    
    // Insights
    insights,
    setInsights,
    pendingInsightsCount,
    markInsightAsRead,
    
    // Suggestions
    hasPendingSuggestions,
    setHasPendingSuggestions,

    // Unread Tracker
    unreadSessions,
    addUnreadSession,
    removeUnreadSession,
    activeConversationId,
    setActiveConversationId,
  }
  
  return (
    <AIContext.Provider value={value}>
      {children}
      {/* Background listeners for recent sessions to detect human agent replies globally */}
      {recentSessionIds.map(id => (
        <AgentWSListener key={id} sessionId={id} onMessage={handleGlobalMessage} />
      ))}
    </AIContext.Provider>
  )
}

